import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import {
  CommonModule
} from '@angular/common';

import {
  FormsModule
} from '@angular/forms';

import {
  ActivatedRoute
} from '@angular/router';

import {
  Subject,
  takeUntil
} from 'rxjs';

import {
  CreatePublicOrderPayload,
  PublicProduct,
  PublicTableResponse,
  PublicTableService
} from '../../core/public-table.service';

import {
  ApiService
} from '../../core/api.service';


interface CartItem {
  product: PublicProduct;
  quantity: number;
  notes: string;
}


type ServiceRequestType =
  | 'WAITER'
  | 'OPEN_SESSION';


@Component({
  selector: 'app-public-table',

  standalone: true,

  imports: [
    CommonModule,
    FormsModule
  ],

  templateUrl:
    './public-table.component.html',

  styleUrl:
    './public-table.component.css'
})
export class PublicTableComponent
  implements OnInit, OnDestroy {

  private readonly destroy$ =
    new Subject<void>();


  publicToken = '';


  data:
    PublicTableResponse |
    null = null;


  products:
    PublicProduct[] = [];


  filteredProducts:
    PublicProduct[] = [];


  cart:
    CartItem[] = [];


  customerName = '';

  customerPhone = '';

  orderNotes = '';

  searchText = '';


  loading = true;

  sendingOrder = false;

  requestingBill = false;

  sendingWaiterRequest = false;

  sendingOpenSessionRequest = false;


  /*
   * Usado para alterar a interface
   * depois que o cliente chama
   * o garçom.
   */
  waiterRequested = false;

  openSessionRequested = false;


  errorMessage = '';

  successMessage = '';


  showCart = false;

  showOrders = false;


  constructor(
    private readonly route:
      ActivatedRoute,

    private readonly publicTableService:
      PublicTableService,

    private readonly api:
      ApiService
  ) {}


  ngOnInit(): void {

    this.route
      .paramMap
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe(
        params => {

          const token =
            params.get(
              'publicToken'
            );


          if (!token) {

            this.loading =
              false;


            this.errorMessage =
              'O endereço da mesa é inválido.';

            return;
          }


          this.publicToken =
            token;


          this.loadTable();

        }
      );
  }


  ngOnDestroy(): void {

    this.destroy$.next();

    this.destroy$.complete();
  }


  /*
   * ==================================================
   * CARREGAR MESA / CARDÁPIO
   * ==================================================
   */

  loadTable(
    showLoading = true
  ): void {

    if (
      !this.publicToken
    ) {
      return;
    }


    if (
      showLoading
    ) {
      this.loading =
        true;
    }


    this.errorMessage =
      '';


    this.publicTableService
      .getTable(
        this.publicToken
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: response => {

          this.data =
            response;


          this.products =
            response.products ||
            [];


          this.filterProducts();


          /*
           * Se a comanda foi aberta
           * depois de uma solicitação,
           * não precisamos mais manter
           * esse aviso local.
           */
          if (
            response.session
          ) {
            this.openSessionRequested =
              false;
          }


          this.loading =
            false;

        },


        error: error => {

          this.loading =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível carregar os dados da mesa.';

        }

      });
  }


  /*
   * ==================================================
   * SOLICITAÇÕES DE ATENDIMENTO
   * ==================================================
   */

  requestWaiter(): void {

    if (
      !this.publicToken ||
      this.sendingWaiterRequest
    ) {
      return;
    }


    this.clearMessages();


    this.sendingWaiterRequest =
      true;


    this.createServiceRequest(
      'WAITER'
    );
  }


  requestOpenSession(): void {

    if (
      !this.publicToken ||
      this.sendingOpenSessionRequest
    ) {
      return;
    }


    if (
      this.hasActiveSession
    ) {

      this.errorMessage =
        'Sua comanda já está aberta.';

      return;
    }


    this.clearMessages();


    this.sendingOpenSessionRequest =
      true;


    this.createServiceRequest(
      'OPEN_SESSION'
    );
  }


  private createServiceRequest(
    type:
      ServiceRequestType
  ): void {

    this.api
      .post<any>(
        `/public/tables/${this.publicToken}/service-requests`,
        {
          type
        }
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: response => {

          if (
            type ===
            'WAITER'
          ) {

            this.sendingWaiterRequest =
              false;


            this.waiterRequested =
              true;


            this.successMessage =
              response?.alreadyPending
                ? 'O garçom já foi chamado. Aguarde um momento.'
                : 'Garçom chamado! Aguarde um momento.';

          } else {

            this.sendingOpenSessionRequest =
              false;


            this.openSessionRequested =
              true;


            this.successMessage =
              response?.alreadyPending
                ? 'A abertura da comanda já foi solicitada.'
                : 'Solicitação enviada! Um atendente abrirá sua comanda.';

          }

        },


        error: error => {

          if (
            type ===
            'WAITER'
          ) {

            this.sendingWaiterRequest =
              false;

          } else {

            this.sendingOpenSessionRequest =
              false;

          }


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível enviar sua solicitação.';

        }

      });
  }


  /*
   * ==================================================
   * PRODUTOS
   * ==================================================
   */

  filterProducts(): void {

    const search =
      this.normalizeText(
        this.searchText
      );


    if (!search) {

      this.filteredProducts = [
        ...this.products
      ];

      return;
    }


    this.filteredProducts =
      this.products
        .filter(
          product => {

            const name =
              this.normalizeText(
                product.name
              );


            const description =
              this.normalizeText(
                product.description ||
                ''
              );


            return (
              name.includes(
                search
              ) ||
              description.includes(
                search
              )
            );

          }
        );
  }


  addToCart(
    product:
      PublicProduct
  ): void {

    this.clearMessages();


    if (
      !this.canOrder
    ) {

      this.errorMessage =
        this.hasActiveSession
          ? 'Esta comanda não aceita novos pedidos.'
          : 'Sua comanda ainda não foi aberta. Solicite atendimento para começar a pedir.';

      return;
    }


    const existingItem =
      this.cart
        .find(
          item =>
            item.product.id ===
            product.id
        );


    if (
      existingItem
    ) {

      if (
        existingItem.quantity >=
        50
      ) {

        this.errorMessage =
          'A quantidade máxima permitida é 50.';

        return;
      }


      existingItem.quantity +=
        1;

    } else {

      this.cart.push({
        product,
        quantity: 1,
        notes: ''
      });

    }


    this.showCart =
      true;
  }


  increaseQuantity(
    item:
      CartItem
  ): void {

    if (
      item.quantity >=
      50
    ) {
      return;
    }


    item.quantity +=
      1;
  }


  decreaseQuantity(
    item:
      CartItem
  ): void {

    if (
      item.quantity <=
      1
    ) {

      this.removeFromCart(
        item.product.id
      );

      return;
    }


    item.quantity -=
      1;
  }


  removeFromCart(
    productId: number
  ): void {

    this.cart =
      this.cart
        .filter(
          item =>
            item.product.id !==
            productId
        );
  }


  clearCart(): void {

    this.cart = [];

    this.orderNotes =
      '';
  }


  /*
   * ==================================================
   * ENVIAR PEDIDO
   * ==================================================
   */

  sendOrder(): void {

    this.clearMessages();


    if (
      !this.canOrder
    ) {

      this.errorMessage =
        'Esta comanda não está disponível para novos pedidos.';

      return;
    }


    if (
      this.cart.length ===
      0
    ) {

      this.errorMessage =
        'Adicione pelo menos um produto ao pedido.';

      return;
    }


    const invalidItem =
      this.cart
        .find(
          item =>
            !Number.isInteger(
              item.quantity
            ) ||
            item.quantity <=
              0
        );


    if (
      invalidItem
    ) {

      this.errorMessage =
        'Existe um item com quantidade inválida.';

      return;
    }


    const payload:
      CreatePublicOrderPayload = {

      customerName:
        this.customerName
          .trim() ||
        undefined,


      customerPhone:
        this.customerPhone
          .trim() ||
        undefined,


      notes:
        this.orderNotes
          .trim() ||
        undefined,


      items:
        this.cart.map(
          item => ({
            productId:
              item.product.id,

            quantity:
              item.quantity,

            notes:
              item.notes
                .trim() ||
              undefined
          })
        )

    };


    this.sendingOrder =
      true;


    this.publicTableService
      .createOrder(
        this.publicToken,
        payload
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: () => {

          this.sendingOrder =
            false;


          this.successMessage =
            'Pedido enviado com sucesso!';


          this.clearCart();


          this.showCart =
            false;


          this.loadTable(
            false
          );

        },


        error: error => {

          this.sendingOrder =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível enviar o pedido.';

        }

      });
  }


  /*
   * ==================================================
   * SOLICITAR CONTA
   * ==================================================
   */

  requestBill(): void {

    this.clearMessages();


    if (
      !this.canRequestBill
    ) {

      this.errorMessage =
        'A conta não pode ser solicitada neste momento.';

      return;
    }


    const confirmed =
      window.confirm(
        'Deseja realmente solicitar a conta? Depois disso, novos pedidos serão bloqueados.'
      );


    if (
      !confirmed
    ) {
      return;
    }


    this.requestingBill =
      true;


    this.publicTableService
      .requestBill(
        this.publicToken
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: () => {

          this.requestingBill =
            false;


          this.successMessage =
            'Conta solicitada! Aguarde o atendimento.';


          this.loadTable(
            false
          );

        },


        error: error => {

          this.requestingBill =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível solicitar a conta.';

        }

      });
  }


  /*
   * ==================================================
   * GETTERS
   * ==================================================
   */

  get cartItemsCount():
    number {

    return this.cart
      .reduce(
        (
          total,
          item
        ) =>
          total +
          item.quantity,
        0
      );
  }


  get cartSubtotal():
    number {

    return this.cart
      .reduce(
        (
          total,
          item
        ) => {

          return (
            total +
            this.toNumber(
              item.product.price
            ) *
            item.quantity
          );

        },
        0
      );
  }


  get canOrder():
    boolean {

    return Boolean(
      this.data
        ?.permissions
        ?.canOrder
    );
  }


  get canRequestBill():
    boolean {

    return Boolean(
      this.data
        ?.permissions
        ?.canRequestBill
    );
  }


  get hasActiveSession():
    boolean {

    return Boolean(
      this.data?.session
    );
  }


  get sessionOrders():
    any[] {

    return (
      this.data
        ?.session
        ?.orders ||
      []
    );
  }


  /*
   * O cliente pode chamar
   * um garçom independentemente
   * de a comanda estar aberta.
   */
  get canCallWaiter():
    boolean {

    return Boolean(
      this.data?.table
    );
  }


  get sessionStatusLabel():
    string {

    const status =
      this.data
        ?.session
        ?.status;


    const labels:
      Record<
        string,
        string
      > = {

      OPEN:
        'Comanda aberta',

      BILL_REQUESTED:
        'Conta solicitada',

      AWAITING_PAYMENT:
        'Aguardando pagamento',

      CLOSED:
        'Comanda encerrada',

      CANCELED:
        'Comanda cancelada'

    };


    return status
      ? labels[status] ||
          status
      : 'Visualizando cardápio';
  }


  getOrderStatusLabel(
    status: string
  ): string {

    const labels:
      Record<
        string,
        string
      > = {

      PENDING:
        'Pedido recebido',

      ACCEPTED:
        'Pedido aceito',

      PREPARING:
        'Em preparação',

      READY:
        'Pedido pronto',

      OUT_FOR_DELIVERY:
        'Saiu para entrega',

      DELIVERED:
        'Entregue',

      CANCELED:
        'Cancelado'

    };


    return (
      labels[status] ||
      status
    );
  }


  /*
   * ==================================================
   * TRACK BY
   * ==================================================
   */

  trackProductById(
    index: number,
    product:
      PublicProduct
  ): number {

    return product.id;
  }


  trackCartItemByProductId(
    index: number,
    item:
      CartItem
  ): number {

    return item.product.id;
  }


  /*
   * ==================================================
   * UTILITÁRIOS
   * ==================================================
   */

  toNumber(
    value:
      number |
      string |
      null |
      undefined
  ): number {

    const parsedValue =
      Number(
        value ||
        0
      );


    return Number
      .isFinite(
        parsedValue
      )
      ? parsedValue
      : 0;
  }


  private normalizeText(
    value: string
  ): string {

    return String(
      value ||
      ''
    )
      .normalize(
        'NFD'
      )
      .replace(
        /[\u0300-\u036f]/g,
        ''
      )
      .toLowerCase()
      .trim();
  }


  private clearMessages():
    void {

    this.errorMessage =
      '';

    this.successMessage =
      '';
  }
}